import type { NavigationRouteMap } from 'domains/navigation';
import type { CatalogSelectorCategory } from 'types/catalog_types';

type ConfigEntry = {
  enabled: boolean;
  value: string;
};

// This is all from application_helper
type FrontEndData = {
  api_root_url: string;
  api_url: string;
  api_token: string;
  session_id: string;
  deploy_env: string;
  is_production: boolean;
  catalog_selector_data: { categories?: CatalogSelectorCategory[] };
  maintenance_message: ConfigEntry;
  site_wide_message: ConfigEntry;
  smarty_api_public_key: string;
  filestack_api_key: string;
  braintree_token: string;
  enable_apple_pay: boolean | null;
  titles: {
    wos: string;
    cart: string;
    projects: string;
    pricetool: string;
    checkout: string;
  };
  mybayphoto: {
    base_url: string;
    oauth_client_id: string;
    oauth_client_secret: string;
  }
};

const frontendDataTag = document.head.querySelector('#frontend_data');
if (!frontendDataTag) {
  throw new Error('Missing frontend_data script tag');
}
if (!frontendDataTag.textContent) {
  throw new Error('Missing frontend_data script tag content');
}

const frontendData: FrontEndData = JSON.parse(frontendDataTag.textContent);

export const API_URL = frontendData.api_url;
export const API_ROOT_URL = frontendData.api_root_url;
export const API_TOKEN = frontendData.api_token;

export const BRAINTREE_TOKEN = frontendData.braintree_token;
export const ENABLE_APPLE_PAY = !!frontendData.enable_apple_pay;

export const DEPLOY_ENV = frontendData.deploy_env;
export const IS_PRODUCTION = frontendData.is_production;
export const SESSION_ID = frontendData.session_id;
export const CATALOG_SELECTOR_DATA = frontendData.catalog_selector_data.categories;
export const FILESTACK_API_KEY = frontendData.filestack_api_key;
export const MAINTENANCE_MESSAGE = frontendData.maintenance_message;
export const SITE_WIDE_MESSAGE = frontendData.site_wide_message;

const titles = frontendData.titles || {};
export const CHECKOUT_TITLE = titles.checkout;
export const DOCUMENT_TITLES: Record<keyof NavigationRouteMap, string | undefined> = {
  cart: titles.cart,
  priceTool: titles.pricetool,
  projects: titles.projects,
  catalogSelector: titles.wos,
  editor: titles.wos,
};

const mybayphoto = frontendData.mybayphoto || {};
export const MYBAYPHOTO_BASE_URL = mybayphoto.base_url;
export const MYBAYPHOTO_OAUTH_CLIENT_ID = mybayphoto.oauth_client_id;
export const MYBAYPHOTO_OAUTH_CLIENT_SECRET = mybayphoto.oauth_client_secret;

export const LOCALSTORAGE_COLLECTION_KEY = 'bpselcol';

export const GIFT_NOTE_RESERVED_COLLECTION = 'bayphoto_giftnoteupload_reserved';
export const OPTIONS_PARAM = 'bpopts';
export const HEIGHT_PARAM = 'height';
export const WIDTH_PARAM = 'width';
export const EDIT_CART_PRODUCT_PARAM = 'cart_product';
export const FILTER_DETAIL_PARAM = 'detail';

export const ROUTE_MAP: NavigationRouteMap = {
  catalogSelector: '/',
  cart: '/cart',
  editor: '/c/',
  priceTool: '/price-tool/',
  projects: '/projects',
};

// Public key to access smarty API
// Access is hostname limited to *.bayphoto.com
// Provided by Elijah Taylor etaylor@circlegraphicsonline.com
// https://www.smarty.com/docs/cloud/authentication#htmlkeys
export const SMARTY_API_PUBLIC_KEY = frontendData.smarty_api_public_key;

const params = new URL(window.location.href).searchParams;
export const paramOptions = params.get(OPTIONS_PARAM);
export const paramHeight = params.get(HEIGHT_PARAM);
export const paramWidth = params.get(WIDTH_PARAM);
export const paramFilterDetail = params.get(FILTER_DETAIL_PARAM) === 'true';

// Grab localstorage values for initial state
export const hideBranding = window.localStorage.getItem('hideBranding') === 'true';
export const showFilenames = window.localStorage.getItem('showFilenames') === 'true';
export const theme = window.localStorage.getItem('theme');
